<template>
  <div class="nutrition-tracker">
    <!-- Login Required Message -->
    <div v-if="!isAuthenticated" class="row">
      <div class="col-12">
        <div class="card border-primary">
          <div class="card-body text-center py-5">
            <div class="mb-4">
              <i class="bi bi-person-lock display-1 text-primary"></i>
            </div>
            <h4 class="card-title mb-3">Login Required</h4>
            <p class="card-text text-muted mb-4">
              Please log in to track your nutrition and monitor your dietary intake.
            </p>
            <div class="d-flex gap-3 justify-content-center">
              <router-link to="/login" class="btn btn-primary px-4">
                <i class="bi bi-box-arrow-in-right me-2"></i>Login
              </router-link>
              <router-link to="/register" class="btn btn-outline-primary px-4">
                <i class="bi bi-person-plus me-2"></i>Sign Up
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Authenticated Content -->
    <div v-else class="row g-4">
      <!-- Daily Summary -->
      <div class="col-lg-4">
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-calendar-day me-2"></i>Today's Summary</h6>
          </div>
          <div class="card-body">
            <div class="nutrition-summary">
              <div class="calorie-circle text-center mb-3">
                <div class="circle-progress">
                  <span class="calorie-number">{{ consumedCalories }}</span>
                  <small class="text-muted d-block">/ {{ targetCalories }} cal</small>
                </div>
              </div>

              <div class="macros-breakdown">
                <div class="macro-item mb-2">
                  <div class="d-flex justify-content-between">
                    <span class="text-success">Protein</span>
                    <span>{{ macros.protein }}g</span>
                  </div>
                  <div class="progress">
                    <div
                      class="progress-bar bg-success"
                      :style="{ width: (macros.protein / targetMacros.protein) * 100 + '%' }"
                    ></div>
                  </div>
                </div>

                <div class="macro-item mb-2">
                  <div class="d-flex justify-content-between">
                    <span class="text-warning">Carbs</span>
                    <span>{{ macros.carbs }}g</span>
                  </div>
                  <div class="progress">
                    <div
                      class="progress-bar bg-warning"
                      :style="{ width: (macros.carbs / targetMacros.carbs) * 100 + '%' }"
                    ></div>
                  </div>
                </div>

                <div class="macro-item">
                  <div class="d-flex justify-content-between">
                    <span class="text-info">Fat</span>
                    <span>{{ macros.fat }}g</span>
                  </div>
                  <div class="progress">
                    <div
                      class="progress-bar bg-info"
                      :style="{ width: (macros.fat / targetMacros.fat) * 100 + '%' }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Add -->
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0"><i class="bi bi-plus-circle me-2"></i>Quick Add</h6>
          </div>
          <div class="card-body">
            <div class="row g-2">
              <div v-for="item in quickAddItems" :key="item.name" class="col-6">
                <button class="btn btn-outline-secondary btn-sm w-100" @click="quickAddFood(item)">
                  <i :class="item.icon + ' me-1'"></i>{{ item.name }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Food Log -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-journal-text me-2"></i>Food Log</h5>
            <button class="btn btn-primary btn-sm" @click="showAddModal = true">
              <i class="bi bi-plus me-1"></i>Add Food
            </button>
          </div>
          <div class="card-body">
            <div class="food-log">
              <!-- Breakfast -->
              <div class="meal-section mb-4">
                <h6 class="meal-title">
                  <i class="bi bi-sunrise me-2"></i>Breakfast
                  <small class="text-muted">({{ getMealCalories('breakfast') }} cal)</small>
                </h6>
                <div
                  v-if="getMealFoods('breakfast').length === 0"
                  class="text-muted text-center py-2"
                >
                  No foods logged for breakfast
                </div>
                <div v-else>
                  <div
                    v-for="food in getMealFoods('breakfast')"
                    :key="food.id"
                    class="food-item d-flex justify-content-between align-items-center p-2 border-bottom"
                  >
                    <div>
                      <strong>{{ food.name }}</strong>
                      <small class="text-muted d-block"
                        >{{ food.serving }} • {{ food.calories }} cal</small
                      >
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFood(food.id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Lunch -->
              <div class="meal-section mb-4">
                <h6 class="meal-title">
                  <i class="bi bi-sun me-2"></i>Lunch
                  <small class="text-muted">({{ getMealCalories('lunch') }} cal)</small>
                </h6>
                <div v-if="getMealFoods('lunch').length === 0" class="text-muted text-center py-2">
                  No foods logged for lunch
                </div>
                <div v-else>
                  <div
                    v-for="food in getMealFoods('lunch')"
                    :key="food.id"
                    class="food-item d-flex justify-content-between align-items-center p-2 border-bottom"
                  >
                    <div>
                      <strong>{{ food.name }}</strong>
                      <small class="text-muted d-block"
                        >{{ food.serving }} • {{ food.calories }} cal</small
                      >
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFood(food.id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Dinner -->
              <div class="meal-section mb-4">
                <h6 class="meal-title">
                  <i class="bi bi-moon me-2"></i>Dinner
                  <small class="text-muted">({{ getMealCalories('dinner') }} cal)</small>
                </h6>
                <div v-if="getMealFoods('dinner').length === 0" class="text-muted text-center py-2">
                  No foods logged for dinner
                </div>
                <div v-else>
                  <div
                    v-for="food in getMealFoods('dinner')"
                    :key="food.id"
                    class="food-item d-flex justify-content-between align-items-center p-2 border-bottom"
                  >
                    <div>
                      <strong>{{ food.name }}</strong>
                      <small class="text-muted d-block"
                        >{{ food.serving }} • {{ food.calories }} cal</small
                      >
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFood(food.id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Snacks -->
              <div class="meal-section">
                <h6 class="meal-title">
                  <i class="bi bi-cookie me-2"></i>Snacks
                  <small class="text-muted">({{ getMealCalories('snacks') }} cal)</small>
                </h6>
                <div v-if="getMealFoods('snacks').length === 0" class="text-muted text-center py-2">
                  No snacks logged
                </div>
                <div v-else>
                  <div
                    v-for="food in getMealFoods('snacks')"
                    :key="food.id"
                    class="food-item d-flex justify-content-between align-items-center p-2 border-bottom"
                  >
                    <div>
                      <strong>{{ food.name }}</strong>
                      <small class="text-muted d-block"
                        >{{ food.serving }} • {{ food.calories }} cal</small
                      >
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFood(food.id)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Food Modal -->
    <div
      v-if="showAddModal"
      class="modal d-block"
      style="background: rgba(0, 0, 0, 0.5)"
      @click.self="showAddModal = false"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Food</h5>
            <button type="button" class="btn-close" @click="showAddModal = false"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="addFood">
              <div class="mb-3">
                <label class="form-label">Food Name</label>
                <input type="text" class="form-control" v-model="foodForm.name" required />
              </div>
              <div class="mb-3">
                <label class="form-label">Serving Size</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="foodForm.serving"
                  placeholder="e.g., 1 cup, 100g, 1 piece"
                  required
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Calories</label>
                <input
                  type="number"
                  class="form-control"
                  v-model="foodForm.calories"
                  min="0"
                  required
                />
              </div>
              <div class="mb-3">
                <label class="form-label">Meal</label>
                <select class="form-select" v-model="foodForm.meal" required>
                  <option value="">Select Meal</option>
                  <option value="breakfast">Breakfast</option>
                  <option value="lunch">Lunch</option>
                  <option value="dinner">Dinner</option>
                  <option value="snacks">Snacks</option>
                </select>
              </div>
              <div class="row">
                <div class="col-md-4">
                  <label class="form-label">Protein (g)</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model="foodForm.protein"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Carbs (g)</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model="foodForm.carbs"
                    min="0"
                    step="0.1"
                  />
                </div>
                <div class="col-md-4">
                  <label class="form-label">Fat (g)</label>
                  <input
                    type="number"
                    class="form-control"
                    v-model="foodForm.fat"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showAddModal = false">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="addFood">Add Food</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useFirebaseAuth } from '@/composables/useFirebaseAuth'

export default {
  name: 'NutritionTracker',
  setup() {
    const { user, isAuthenticated, initAuth } = useFirebaseAuth()
    return { user, isAuthenticated, initAuth }
  },
  data() {
    return {
      foods: [],
      showAddModal: false,
      targetCalories: 2000,
      targetMacros: {
        protein: 150,
        carbs: 250,
        fat: 67,
      },
      foodForm: {
        name: '',
        serving: '',
        calories: '',
        meal: '',
        protein: '',
        carbs: '',
        fat: '',
      },
      quickAddItems: [
        { name: 'Water', icon: 'bi bi-droplet' },
        { name: 'Coffee', icon: 'bi bi-cup-hot' },
        { name: 'Apple', icon: 'bi bi-apple' },
        { name: 'Banana', icon: 'bi bi-circle' },
        { name: 'Almonds', icon: 'bi bi-circle-fill' },
        { name: 'Yogurt', icon: 'bi bi-cup' },
      ],
    }
  },

  computed: {
    consumedCalories() {
      return this.foods.reduce((total, food) => total + (food.calories || 0), 0)
    },

    macros() {
      return this.foods.reduce(
        (totals, food) => ({
          protein: totals.protein + (food.protein || 0),
          carbs: totals.carbs + (food.carbs || 0),
          fat: totals.fat + (food.fat || 0),
        }),
        { protein: 0, carbs: 0, fat: 0 }
      )
    },
  },

  mounted() {
    this.initAuth()
    if (this.isAuthenticated) {
      this.loadFoods()
    }
  },

  methods: {
    loadFoods() {
      const today = new Date().toISOString().split('T')[0]
      const stored = localStorage.getItem(`foods_${today}`)
      if (stored) {
        this.foods = JSON.parse(stored)
      }
    },

    saveFoods() {
      const today = new Date().toISOString().split('T')[0]
      localStorage.setItem(`foods_${today}`, JSON.stringify(this.foods))
    },

    addFood() {
      const newFood = {
        id: Date.now(),
        ...this.foodForm,
        calories: parseFloat(this.foodForm.calories),
        protein: parseFloat(this.foodForm.protein || 0),
        carbs: parseFloat(this.foodForm.carbs || 0),
        fat: parseFloat(this.foodForm.fat || 0),
      }

      this.foods.push(newFood)
      this.saveFoods()
      this.resetForm()
      this.showAddModal = false
    },

    removeFood(id) {
      this.foods = this.foods.filter((food) => food.id !== id)
      this.saveFoods()
    },

    resetForm() {
      this.foodForm = {
        name: '',
        serving: '',
        calories: '',
        meal: '',
        protein: '',
        carbs: '',
        fat: '',
      }
    },

    quickAddFood(item) {
      this.foodForm.name = item.name
      this.foodForm.serving = '1 serving'

      // Pre-fill some default values
      const defaults = {
        Water: { calories: 0, meal: 'snacks' },
        Coffee: { calories: 5, meal: 'breakfast' },
        Apple: { calories: 95, meal: 'snacks', carbs: 25 },
        Banana: { calories: 105, meal: 'snacks', carbs: 27 },
        Almonds: { calories: 160, meal: 'snacks', fat: 14, protein: 6 },
        Yogurt: { calories: 100, meal: 'snacks', protein: 10 },
      }

      const defaultValues = defaults[item.name] || { calories: 100, meal: 'snacks' }
      Object.assign(this.foodForm, defaultValues)

      this.showAddModal = true
    },

    getMealFoods(meal) {
      return this.foods.filter((food) => food.meal === meal)
    },

    getMealCalories(meal) {
      return this.getMealFoods(meal).reduce((total, food) => total + food.calories, 0)
    },
  },
}
</script>

<style scoped>
.calorie-circle {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 120px;
  height: 120px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.calorie-number {
  font-size: 1.5rem;
  font-weight: bold;
}

.macro-item .progress {
  height: 6px;
  border-radius: 3px;
}

.meal-title {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.food-item {
  transition: background-color 0.3s ease;
}

.food-item:hover {
  background-color: #f8f9fa;
}

.modal {
  z-index: 1050;
}

@media (max-width: 768px) {
  .calorie-circle {
    width: 100px;
    height: 100px;
  }

  .calorie-number {
    font-size: 1.2rem;
  }

  .food-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .food-item button {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}
</style>
